import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { EventsService } from "../../../_services/eventsServices/events.service";
import { FormGroup, FormGroupDirective } from "@angular/forms";
import { FormQuestion } from "../event-page-models/form-question";
import { createParentEventFormQuestionsContent } from "../create-parent-event-form-questions-content/create-parent-event-form-questions-content";

import { GenericFormComponent } from "../../../form/generic-form/generic-form.component";
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { SelectMenuOption } from "../../../form/select-menu/select-menu.component";
import { EventFormNoalData } from "../event-page-models/event-form-noal-data";
import { EventFormInitialData } from "../event-page-models/event-form-initial-data";
import { EventFormStatusEnum } from "../event-page-models/event-form-status.enum";
import { sleep } from "../../../_helpers/sleep";
import { EventSerializerService } from "../../../_services/eventsServices/eventSerializer.service";
import { Subject } from "rxjs/Subject";
import { takeUntil } from "rxjs/operators";
import { Observable } from "rxjs/Observable";
import { EventFormDynamicField } from "../event-page-models/event-form-dynamic-field";
import { ParentEventIdType } from "../event-page-models/parent-event-id-type";
import { EventFormCustomFormType } from "../event-page-models/event-form-custom-form-type";
import { SpecialTypeEventsService } from "../../../_services/eventsServices/specialTypeEvents.service";
import { SessionManagerService } from "../../../_services/SessionManager/session-manager.service";

@Component({
  selector: "parent-event-form-main-details",
  templateUrl: "./create-parent-event-form-main-details.component.html",
  styleUrls: ["./create-parent-event-form-main-details.component.scss"],
  animations: [
    trigger("openClose", [
      state(
        "open",
        style({
          height: "90px",
          maxHeight: "2000px",
          overflow: "visible",
          opacity: 1,
        })
      ),
      state(
        "closed",
        style({
          height: "1px",
          opacity: 0,
          overflow: "hidden",
        })
      ),
      transition("open => closed", [animate("0.2s")]),
      transition("* => open", [animate("0.2s")]),
      transition("closed => open", [animate("0.2s")]),
    ]),
  ],
})
export class CreateParentEventFormMainDetailsComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input("eventFormCustomFormType")
  public eventFormCustomFormType: EventFormCustomFormType =
    EventFormCustomFormType.common;
  public readonly parentEventDetailsData$: BehaviorSubject<FormQuestion[]> =
    new BehaviorSubject<FormQuestion[]>(
      SpecialTypeEventsService.deepCopyFormQuestions(
        createParentEventFormQuestionsContent.parentEventDetailsQuestions
      )
    );

  public parentEventAdvancedData: FormQuestion[] =
    SpecialTypeEventsService.deepCopyFormQuestions(
      createParentEventFormQuestionsContent.parentEventAdvancedDetailsQuestions
    );
  public parentEventDetailsFormGroup: FormGroup | null = null;
  public parentEventAdvancedDetailsFormGroup: FormGroup | null = null;
  public dynamicFieldsChanges: Observable<EventFormDynamicField[]> =
    this.eventsService.getEventFormDynamicFields();
  public advanceDetailsOpen: boolean = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private eventsService: EventsService,
    private formInput: FormGroupDirective
  ) {
    this.eventsService.eventFormInitialData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((eventFormInitialData: EventFormInitialData) => {
        if (!eventFormInitialData) {
          return;
        }
        const updatedQuestionsOptions =
          CreateParentEventFormMainDetailsComponent.UpdateFormQuestionsWithNoalServerData(
            SpecialTypeEventsService.deepCopyFormQuestions(
              createParentEventFormQuestionsContent.parentEventDetailsQuestions
            ),
            eventFormInitialData.eventFormNoalData
          );
        if (
          eventFormInitialData.formStatus === EventFormStatusEnum.Update ||
          EventFormStatusEnum.Copy
        ) {
          this.patchFormValues(eventFormInitialData).then();
        }
        this.parentEventDetailsData$.next(updatedQuestionsOptions);
      });
  }
  public static UpdateFormQuestionsWithNoalServerData(
    parentEventDetailsQuestions: FormQuestion[],
    noalFormData: EventFormNoalData
  ): FormQuestion[] {
    const newQuestions = parentEventDetailsQuestions.map((q) => ({ ...q }));
    newQuestions.forEach((q) => {
      if (q.fieldName === "eventType") {
        const newOptions: SelectMenuOption[] = noalFormData.event_types
          .map((evType) => {
            return {
              key: evType.id.toString(),
              text: evType.name,
              customValue: null,
              icon: null,
            };
          })
          .filter(
            (et) => et.key !== EventFormCustomFormType.Michlol.toString()
          );
        q.selectItems = newOptions;
      } else if (q.fieldName === "brothersDiscountPeriod") {
        const newOptions: SelectMenuOption[] =
          noalFormData.registration_periods.map((evType) => {
            return {
              key: evType.id.toString(),
              text: evType.name,
              customValue: null,
              icon: null,
            };
          });
        q.selectItems = newOptions;
      } else if (q.fieldName === "eventCollection") {
        const newOptions: SelectMenuOption[] = noalFormData.eventCollection.map(
          EventSerializerService.eventCollectionItemToSelectMenuOption
        );
        q.selectItems = newOptions;
      }
    });
    return newQuestions;
  }
  public ngOnInit(): void {
    this.buildForm();
  }

  public openCloserAdvanced() {
    this.advanceDetailsOpen = !this.advanceDetailsOpen;
  }

  public ngAfterViewInit() {}

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  public customFormQuestionsModifier(
    formQuestions: FormQuestion[],
    typeOfQuestion: "parentAdvance" | "parentDetails"
  ): FormQuestion[] {
    return SpecialTypeEventsService.specialEventsFormQuestionsModifier(
      formQuestions,
      typeOfQuestion,
      this.eventFormCustomFormType
    );
  }

  private buildForm(): void {
    this.parentEventDetailsFormGroup = GenericFormComponent.buildFormGroup(
      this.parentEventDetailsData$.value
    );

    this.parentEventAdvancedDetailsFormGroup =
      GenericFormComponent.buildFormGroup(this.parentEventAdvancedData);

    this.formInput.form.addControl(
      "parentEventDetails",
      this.parentEventDetailsFormGroup
    );
    this.formInput.form.addControl(
      "parentEventAdvancedDetails",
      this.parentEventAdvancedDetailsFormGroup
    );
  }

  private async patchFormValues(eventFormInitialData: EventFormInitialData) {
    if (this.formInput.form && this.formInput.form.controls) {
    } else {
      await sleep(100);
    }

    const parentEvent = eventFormInitialData.eventFormParentEventData;

    const selectedEventType =
      eventFormInitialData.eventFormNoalData.event_types.find(
        (e) => e.is_selected
      );
    const brotherDiscountPeriod =
      eventFormInitialData.eventFormNoalData.registration_periods.find(
        (e) => e.is_selected
      );

    const eventCollectionSelectedOptions =
      eventFormInitialData.eventFormNoalData.eventCollection.filter(
        (e) => e.isSelected
      );

    const parentEventId: ParentEventIdType =
      eventFormInitialData.eventFormParentEventData.id || "new";

    const valueToPatchEventDetails = {
      parentEventId,
      eventName: parentEvent.name,
      eventType: selectedEventType
        ? {
            customValue: selectedEventType.id,
            icon: null,
            key: selectedEventType.id,
            text: selectedEventType.name,
          }
        : "",
      eventStartDate: EventSerializerService.EpocToDateAndTime(
        parentEvent.event_start_date
      ).datePicker,
      eventStartHour: EventSerializerService.EpocToDateAndTime(
        parentEvent.event_start_date
      ).hoursPicker,
      eventEndDate: EventSerializerService.EpocToDateAndTime(
        parentEvent.event_end_date
      ).datePicker,
      eventEndHour: EventSerializerService.EpocToDateAndTime(
        parentEvent.event_end_date
      ).hoursPicker,
      registrationStartDate: EventSerializerService.EpocToDateAndTime(
        parentEvent.registration_start_date
      ).datePicker,
      registrationEndDate: EventSerializerService.EpocToDateAndTime(
        parentEvent.registration_end_date
      ).datePicker,
      eventDescription: parentEvent.description,
      closedToMembers: parentEvent.tax_payers_only,
      brothersDiscount: parentEvent.has_brother_discount,
      brothersDiscountPeriod: brotherDiscountPeriod
        ? {
            customValue: brotherDiscountPeriod.id,
            icon: null,
            key: brotherDiscountPeriod.id,
            text: brotherDiscountPeriod.name,
          }
        : "",
      eventCollection: eventCollectionSelectedOptions.map(
        EventSerializerService.eventCollectionItemToSelectMenuOption
      ),
    };

    this.formInput.form.controls["parentEventDetails"].patchValue(
      valueToPatchEventDetails
    );

    const valueToPatchAdvancedDetails = {
      eventCode: parentEvent.code,
      eventIndex: parentEvent.priority_index,
      gainCenter1: parentEvent.merkaz_revach_1,
      gainCenter3: parentEvent.merkaz_revach_3,
    };
    this.formInput.form.controls["parentEventAdvancedDetails"].patchValue(
      valueToPatchAdvancedDetails
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/admin/events-screen/create-parent-event-form-main-details/create-parent-event-form-main-details.component.ts